import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, Gift, Users, Package, LogOut, Crown, Sparkles, Banknote, Shield, Building2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText } from "@/lib/api";
import AdminDashboard from "./AdminDashboard";
import AdminGifts from "./AdminGifts";
import AdminUsers from "./AdminUsers";
import AdminPackages from "./AdminPackages";
import AdminTiers from "./AdminTiers";
import AdminEffects from "./AdminEffects";
import AdminPayouts from "./AdminPayouts";
import AdminModeration from "./AdminModeration";
import AdminAgencies from "./AdminAgencies";

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/login", { email, password });
      localStorage.setItem("yaara_admin_token", data.token);
      localStorage.setItem("yaara_admin_role", data.admin?.role || "admin");
      if (data.admin?.role === "gift_editor") {
        navigate("/admin/gifts");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 admin-dark">
      <form onSubmit={submit} className="w-full max-w-sm glass rounded-3xl p-8">
        <h1 className="text-xl font-extrabold font-display gradient-text mb-1">Yaara Live Admin</h1>
        <p className="text-zinc-500 text-xs mb-6">Control center login</p>
        <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
          className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-4 py-3 outline-none text-white text-sm mb-3 focus:border-violet-500" />
        <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
          className="w-full bg-[#1A1A24] border border-white/10 rounded-xl px-4 py-3 outline-none text-white text-sm mb-5 focus:border-violet-500" />
        <button data-testid="admin-login-button" disabled={busy}
          className="w-full gradient-primary text-white font-bold py-3 rounded-xl disabled:opacity-40">
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

const NAV = [
  { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", testId: "admin-nav-dashboard" },
  { path: "/admin/gifts", icon: Gift, label: "Gift Manager", testId: "admin-nav-gifts" },
  { path: "/admin/users", icon: Users, label: "Users", testId: "admin-nav-users" },
  { path: "/admin/packages", icon: Package, label: "Coin Packages", testId: "admin-nav-packages" },
  { path: "/admin/tiers", icon: Crown, label: "Tier Manager", testId: "admin-nav-tiers" },
  { path: "/admin/effects", icon: Sparkles, label: "Entry Effects", testId: "admin-nav-effects" },
  { path: "/admin/payouts", icon: Banknote, label: "Payouts & KYC", testId: "admin-nav-payouts" },
  { path: "/admin/moderation", icon: Shield, label: "Moderation", testId: "admin-nav-moderation" },
  { path: "/admin/agencies", icon: Building2, label: "Agencies", testId: "admin-nav-agencies" },
];

function AdminLayout({ children, userRole }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGiftEditor = userRole === "gift_editor";
  const navItems = isGiftEditor
    ? NAV.filter((item) => item.path === "/admin/gifts")
    : NAV;

  return (
    <div className="min-h-screen flex admin-dark">
      <aside className="w-56 shrink-0 border-r border-white/10 p-4 flex flex-col">
        <h1 className="font-extrabold font-display gradient-text text-lg px-2 mb-8">
          {isGiftEditor ? "Gift Admin" : "Yaara Admin"}
        </h1>
        <nav className="space-y-1 flex-1">
          {navItems.map(({ path, icon: Icon, label, testId }) => (
            <button key={path} data-testid={testId} onClick={() => navigate(path)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === path ? "gradient-primary text-white" : "text-zinc-400 hover:bg-white/5"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout-button"
          onClick={() => {
            localStorage.removeItem("yaara_admin_token");
            localStorage.removeItem("yaara_admin_role");
            navigate("/admin");
          }}
          className="flex items-center gap-2 text-zinc-500 text-xs font-semibold px-3 py-2">
          <LogOut size={14} /> Logout
        </button>
      </aside>
      <main className="flex-1 p-6 max-w-7xl overflow-x-auto">{children}</main>
    </div>
  );
}

function Guard({ children }) {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("yaara_admin_token");
    if (!token) {
      setLoading(false);
      return;
    }
    adminApi
      .get("/admin/me")
      .then(({ data }) => {
        const role = data.admin?.role || "admin";
        setUserRole(role);
        localStorage.setItem("yaara_admin_role", role);
      })
      .catch(() => {
        setUserRole(false);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!userRole) return <Navigate to="/admin" replace />;

  if (userRole === "gift_editor" && pathname !== "/admin/gifts") {
    return <Navigate to="/admin/gifts" replace />;
  }

  return <AdminLayout userRole={userRole}>{children}</AdminLayout>;
}

export default function Admin() {
  return (
    <Routes>
      <Route index element={<AdminLogin />} />
      <Route path="dashboard" element={<Guard><AdminDashboard /></Guard>} />
      <Route path="gifts" element={<Guard><AdminGifts /></Guard>} />
      <Route path="users" element={<Guard><AdminUsers /></Guard>} />
      <Route path="packages" element={<Guard><AdminPackages /></Guard>} />
      <Route path="tiers" element={<Guard><AdminTiers /></Guard>} />
      <Route path="effects" element={<Guard><AdminEffects /></Guard>} />
      <Route path="payouts" element={<Guard><AdminPayouts /></Guard>} />
      <Route path="moderation" element={<Guard><AdminModeration /></Guard>} />
      <Route path="agencies" element={<Guard><AdminAgencies /></Guard>} />
    </Routes>
  );
}

